<script>
import dayjs from 'dayjs';
import columns from '@/pages/promotions/promoCodes/columns';
import { mapActions, mapState } from 'vuex';
import createDebounce from '@/utils/debounce';
import locale from 'ant-design-vue/es/date-picker/locale/en_US';
import createOrEditPromoCode from '@/pages/promotions/promoCodes/createOrEditPromoCode/Create-or-edit-promo-code.vue';
import { SearchOutlined } from '@ant-design/icons-vue';

export default {
  components: { SearchOutlined, createOrEditPromoCode },
  data() {
    return {
      columns: columns(),
      showModal: false,
      showEdit: false,
      selectedId: null,
      current: 1,
      pageSize: 10,
      offset: 0,
      searchText: '',
      selectedCategory: undefined,
      selectedInterval: [],
      categoriesList: [],
      formatDate: 'YYYY-MM-DD',
      locale,
    };
  },
  props: {
    checkedCategory: {
      type: Number,
      required: true,
    },
  },
  methods: {
    ...mapActions({
      getCategoriesAction: 'promotions/getPromoCategories',
      searchPromoCodesAction: 'promotions/searchPromoCodes',
      deletePromoCodeAction: 'promotions/deletePromoCode',
    }),
    dayjs,
    createDebounce,
    openNewModal() {
      this.showModal = true;
    },
    openModalWindow(id) {
      this.selectedId = id;
      this.showEdit = true;
    },
    getCategories() {
      const payload = {
        limit: this.pageSize,
        offset: this.offset,
      };
      this.getCategoriesAction(payload);
    },
    setPagination(count) {
      this.current = count;
    },
    searchPromoCodes() {
      const params = {
        limit: this.pageSize,
        offset: this.offset,
        ...(this.searchText ? { s: this.searchText } : {}),
        ...((this.selectedCategory && this.selectedCategory.length)
        || (this.selectedInterval && this.selectedInterval.length)
          ? {
            f: JSON.stringify({
              ...(this.selectedCategory && this.selectedCategory.length
                ? { categoryId: this.selectedCategory }
                : {}),
              ...(this.selectedInterval && this.selectedInterval.length
                ? {
                  startDate: `${dayjs(this.selectedInterval[0]).format(this.formatDate)}T00:00:00`,
                  endDate: `${dayjs(this.selectedInterval[1]).format(this.formatDate)}T23:59:59`,
                }
                : {}),
            }),
          }
          : {}
        ),
      };
      this.searchPromoCodesAction(params);
    },
    deletePromoCode(id) {
      const payload = {
        id,
        params: {
          limit: this.pageSize,
          offset: this.offset,
          ...(this.searchText ? { s: this.searchText } : {}),
        },
      };
      this.deletePromoCodeAction(payload);
    },
  },
  mounted() {
    if (this.checkedCategory) {
      this.selectedCategory = [...this.selectedCategory || [], this.checkedCategory];
    }
    this.getCategories();
    this.searchPromoCodes();
  },
  computed: {
    ...mapState({
      categories: (state) => state.promotions.categories,
      promoCodes: (state) => state.promotions.promoCodes,
      promoCodesTotal: (state) => state.promotions.promoCodesTotal,
    }),
  },
  watch: {
    current(newValue) {
      this.offset = (newValue - 1) * this.pageSize;
      this.searchPromoCodes();
    },
    checkedCategory(newV) {
      if (newV) {
        this.selectedCategory = [...this.selectedCategory || [], newV];
      }
    },
    searchText() {
      if (this.current === 1) {
        this.offset = 0;
      }
      this.current = 1;
      createDebounce(this.searchPromoCodes(), 1000);
    },
    pageSize() {
      if (this.current === 1) {
        this.offset = 0;
        this.searchPromoCodes();
      }
      this.current = 1;
    },
    categories() {
      this.categoriesList = this.categories.map((item) => ({
        label: item.name,
        value: item.id,
      }));
    },
    selectedCategory() {
      if (this.current === 1) {
        this.offset = 0;
      }
      this.current = 1;
      this.searchPromoCodes();
    },
    selectedInterval(newV) {
      if (this.current === 1) {
        this.offset = 0;
      }
      this.current = 1;
      if (newV === null) {
        this.selectedInterval = [];
      }
      this.searchPromoCodes();
    },
  },
};
</script>

<template>
  <div class="create-button">
    <a-range-picker v-model:value="selectedInterval" :format="formatDate" :locale="locale" />
    <a-select
      mode="multiple"
      allowClear
      style="min-width: 350px"
      :options="categoriesList"
      v-model:value="selectedCategory"
      placeholder="Select categories..."
    />
    <a-input
      placeholder="Search by name..."
      style="max-width: 350px"
      v-model:value="searchText"
    >
      <template #suffix>
        <search-outlined />
      </template>
    </a-input>
    <a-button
      type="primary"
      html-type="button"
      @click="openNewModal"
    >
      Create promo code
    </a-button>
  </div>
  <div v-if="showModal">
    <create-or-edit-promo-code
      v-model:show="showModal"
      :page-size="pageSize"
      :offset="offset"
      :categories-list="categoriesList"
    />
  </div>
  <a-table
    :columns="columns"
    :dataSource="promoCodes"
    :pagination="false"
  >
    <template #bodyCell="{ column, record }">
      <template v-if="column.dataIndex === 'activeIntervals'">
        <div class="column">
          <div v-for="interval in record.activeIntervals" :key="interval.id">
            {{ `${dayjs(interval.startDate).format(formatDate)}
            - ${dayjs(interval.endDate).format(formatDate)}` }}
          </div>
        </div>
      </template>
      <template v-if="column.dataIndex === 'actions'">
        <div class="action-buttons" v-if="record.isDeleted !== true">
          <a-button
            type="primary"
            html-type="button"
            @click="openModalWindow(record.id)"
          >
            Edit
          </a-button>
          <div v-if="showEdit && selectedId && selectedId === record.id">
            <create-or-edit-promo-code
              v-model:show="showEdit"
              :page-size="pageSize"
              :offset="offset"
              :promo-code-id="record.id"
              :categories-list="categoriesList"
            />
          </div>
          <a-popconfirm
            title="Sure to delete?"
            @confirm="deletePromoCode(record.id)"
          >
            <a-button type="primary" html-type="button" danger>
              Delete
            </a-button>
          </a-popconfirm>
        </div>
      </template>
    </template>
  </a-table>
  <div class="pagination" >
    <a-pagination
      v-model:current="current"
      v-model:pageSize="pageSize"
      :total="promoCodesTotal"
      show-less-items
      @change="setPagination"
    />
  </div>
</template>

<style scoped lang="scss">
.create-button {
  display: flex;
  justify-content: space-between;
  padding: 10px 20px;
}
.sort-button {
  margin-right: auto;
}
.action-buttons {
  display: flex;
  flex-direction: row;
}
.pagination {
  display: flex;
  justify-content: flex-end;
  padding: 10px 20px;
}
.column {
  display: flex;
  flex-direction: column;
}

button:first-child {
  margin-right: 20px;
}
</style>
